import { USERDB } from '~/app/constants.js';
import { isStateCodeInvalid } from '~/app/Utility/general.js';

export const getListLabels = dbType => {
  switch (dbType) {
    case USERDB.ZIPCODE:
      return {
        idColHeader: 'Zip Code',
        dataColHeader: 'Location',
        dbListTitle: 'Zip Codes',
      };
    case USERDB.EFIN:
      return {
        idColHeader: 'EFIN',
        dataColHeader: 'Company Name',
        dbListTitle: 'EF Originators',
      };
    case USERDB.EMPLOYER:
      return {
        idColHeader: 'EIN',
        dataColHeader: 'Employer Name',
        dbListTitle: 'Employers/Payers',
      };
    case USERDB.CAREPROVIDER:
      return {
        idColHeader: 'Provider Code',
        dataColHeader: 'Provider Name',
        dbListTitle: 'Care Providers',
      };
    case USERDB.REFERRALS:
      return {
        idColHeader: 'Referral Name',
        dataColHeader: '',
        dbListTitle: 'Referrals',
      };
    case USERDB.DONEE:
      return {
        idColHeader: 'EIN',
        dataColHeader: 'Donee Information',
        dbListTitle: 'Donees',
      };
    case USERDB.BANKRTN:
      return {
        idColHeader: 'Routing Number',
        dataColHeader: 'Bank Name',
        dbListTitle: 'Bank RTNs',
      };
    case USERDB.OCCUPATION:
      return {
        idColHeader: 'Occupation',
        dataColHeader: '',
        dbListTitle: 'Occupations',
      };
    case USERDB.SITEIDENTIFIERS:
      return {
        idColHeader: 'Site Code',
        dataColHeader: 'Site Description',
        dbListTitle: 'Site Identifiers',
      };
    case USERDB.USERSTATUSCODES:
      return {
        idColHeader: 'Status Code',
        dataColHeader: 'Status Code Description',
        dbListTitle: 'User Status Codes',
      };
    case USERDB.EDUINST:
      return {
        idColHeader: 'EIN',
        dataColHeader: 'Institution Name',
        dbListTitle: 'Educational Institutions',
      };
  }
};

export const sidebarItems = {
  citiesAndZipcodes: {
    description: 'Cities/Zip Codes',
    path: '/database/citiesandzip',
  },
  employersAndPayers: {
    description: 'Employers/Payers',
    path: '/database/employersandpayers',
  },
  careProviders: {
    description: 'Care Providers',
    path: '/database/careproviders',
  },
  referrals: {
    description: 'Referrals',
    path: '/database/referrals',
  },
  donees: {
    description: 'Donees',
    path: '/database/donees',
  },
  bankRTNs: {
    description: 'Bank RTNs',
    path: '/database/bankrtns',
  },
  occupations: {
    description: 'Occupations',
    path: '/database/occupations',
  },
  siteIdentifiers: {
    description: 'Site Identifiers',
    path: '/database/siteidentifiers',
  },
  userStatusCodes: {
    description: 'User Status Codes',
    path: '/database/userstatuscodes',
  },
  educationalInstitutions: {
    description: 'Educational Institutions',
    path: '/database/educationalinstitutions',
  },
};

export const formHasErrors = (fields, handleSetErrors) => {
  const errors = {};
  for (const field of Object.keys(fields)) {
    const value = fields[field];
    switch (field) {
      case 'state_code':
        if (isStateCodeInvalid(value) && !fields?.hidden_domestic_ind) {
          // Only check the validity of State Code if Domestic Address
          errors[field] = `State Code is Invalid`;
        }
        break;
      case 'state_code_1':
        // Following the logic in place, state_Code_1 & 2 are not required fields so errors will only appear on user input
        if (isStateCodeInvalid(value) && value !== '') errors[field] = `State Code is Invalid`;
        break;
      case 'state_code_2':
        if (isStateCodeInvalid(value) && value !== '') errors[field] = `State Code is Invalid`;
        break;
      case 'city':
        if (/[^0-9a-zA-Z ]/.test(value)) errors[field] = `City contains invalid Characters`;
        break;
      case 'zip_code':
      case 'postal_code':
        // domestic zipcode
        if (!/^(\d{5}|\d{9}|\d{12})$/.test(value) && !fields?.hidden_domestic_ind) {
          errors[field] = `Zip Code must be exactly 5, 9, or 12 digits and contain only numbers`;

          // foreign zipcode
          // TODO: Our database table uses the same column for both foreign and domestic zip codes, but foreign zip codes allow up to 50 characters
          // If the user attempts to save with a 12+ digit zip code, it will return a DB error. This is a pre-existing issue that I don’t think our users are triggering.
          // For now, so the user does not encounter errors, we should only allow up to 12 digits for foreign zip codes until we have a refactor in place.
        } else if (!/^\d{5,12}$/.test(value)) {
          errors[
            field
          ] = `Foreign Zip Code can be between 5 and 12 digits and contain only numbers`;
        }
        break;
      case 'employer_ein':
      case 'bank_rtn':
      case 'institution_ein':
      case 'ein':
      case 'provider_code':
        if (/[^0-9 ]/.test(value)) errors[field] = `Entry contains invalid Characters`;
        break;
      case 'address1':
        // Specifically address1 on DB modals with a foreign address are allowed extra special characters
        // Logic will need to be updated if that exception changes or a new special characters are allowed/disallowed
        if (fields?.hidden_foreign_ind) {
          if (/[^0-9a-zA-Z/ ,&-]/.test(value)) errors[field] = `Address contains invalid Characters`;
        } else if (/[^0-9a-zA-Z/ ]/.test(value))
          errors[field] = `Address contains invalid Characters`;
        break;
      case 'address2':
        if (/[^0-9a-zA-Z/ ]/.test(value)) errors[field] = `Address contains invalid Characters`;
        break;
      case 'firmname':
        if (/[`!@#$^*()_=[\]{};':"\\|.<>?~]/.test(value))
          errors[field] = `Entry contains invalid Characters`;
        break;
      case 'provider_name':
      case 'provider_name_ctrl':
      case 'firmnamectrl':
      case 'donee_name':
      case 'bank_name':
      case 'institution_name':
      case 'occupation':
      case 'site_description':
      case 'status_description':
      case 'country_code':
      case 'province':
      case 'state_id_1':
      case 'state_id_2':
      case 'site_code':
      case 'status_code':
      case 'district':
        if (/[`!@#$^*()_/=[\]{};':"\\|.<>?~]/.test(value))
          errors[field] = `Entry contains invalid Characters`;
        break;
    }
  }

  handleSetErrors(errors); // set any errors to the local state
  return Object.keys(errors).length !== 0; // if even one error exists, prevent user from submitting
};
